import React, { useEffect, useState } from 'react'; // <-- IMPORT useState
import { useDispatch, useSelector } from 'react-redux';
import ProductCard from '../ProductCard/ProductCard';
import { setSearchResults, setSearchWindow, setSearchQuery } from '../../Redux Store/Slice';
import SearchSkeleton from './SearchSkeleton'; // <-- IMPORT SKELETON
import './Search.css';
import { Link } from 'react-router-dom';

const Search = () => {
    const dispatch = useDispatch();
    const { isSearchWindowOpen, searchQuery, searchResults } = useSelector(state => state.appstate);

    // ⭐ NEW LOCAL STATE FOR LOADING
    const [loading, setLoading] = useState(false); 

    const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        if (!isSearchWindowOpen) return;

        // 1. CLEAR RESULTS/STOP LOADING if query is empty
        if (searchQuery.length === 0) {
            dispatch(setSearchResults({ products: [], collections: [] }));
            setLoading(false); // Ensure loading is false
            return; 
        }

        // 2. START LOADING when a query is typed
        setLoading(true); 

        // 3. Debounce the API call
        const debounceTimer = setTimeout(() => {
            fetchSearchResults(searchQuery);
        }, 300); 

        return () => clearTimeout(debounceTimer);
    }, [searchQuery, dispatch, isSearchWindowOpen]); // Added isSearchWindowOpen

    const fetchSearchResults = async (query) => {
        try {
            // ... (your existing fetch logic)
            const response = await fetch(`${API_BASE_URL}/search/${query}`);

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            const formattedProducts = data.products.map(product => ({
                // ... (product formatting)
                title: product.name,
                slug: product.slug,
                color: product.color,
                frontImage: product.image_url,
                backImage: product.extra_images?.[0] || product.image_url,
                price: parseFloat(product.base_price),
                originalPrice: product.price_override ? parseFloat(product.price_override) : null,
                discount: null,
                soldOut: product.variants.every(v => v.stock_quantity === 0)
            }));

            dispatch(setSearchResults({ products: formattedProducts, collections: data.collections }));

        } catch (error) {
            console.error("Failed to fetch search results:", error);
            dispatch(setSearchResults({ products: [], collections: [] }));
        } finally {
            // 4. STOP LOADING regardless of success or failure
            setLoading(false); 
        }
    };

    const handleClear = () => {
        dispatch(setSearchQuery(""));
        dispatch(setSearchWindow(false));
        setLoading(false); // Ensure loading is reset on clear/close
    };

    if (!isSearchWindowOpen) {
        return null;
    }

    // ⭐ CONDITIONAL RENDER FOR CONTENT
    let content;

    if (loading) {
        // Show skeleton while waiting for debounced fetch results
        content = <SearchSkeleton />; 
    } else if (searchQuery.length === 0) {
        // Show a prompt when nothing is typed
        content = <p className="no-results-text">Start typing to see results...</p>;
    } else if (searchResults.products.length === 0 && searchResults.collections.length === 0) {
        // Show No Results if the fetch completed and both arrays are empty
        content = <p className="no-results-text">No results found for "{searchQuery}".</p>;
    } else {
        // Show actual results
        content = (
            <>
                <div className="search-suggestions">
                    <h3>Suggestions</h3>
                    <ul>
                        {searchResults.collections.map(collection => (
                            <li key={collection.collection_id}>
                                <Link to={"/collection/" + collection.slug} onClick={handleClear}><span className="suggestion-text">{collection.title}</span></Link>
                                <span className="suggestion-pill">collection</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="search-results">
                    <h3>Products ({searchResults.products.length})</h3>
                    <div className="product-grid">
                        {searchResults.products.map(product => (
                            <ProductCard 
                                key={product.slug} 
                                product={product} 
                                onClick={handleClear} 
                            />
                        ))}
                    </div>
                </div>
            </>
        );
    }


    return (
        <div className="search-overlay">
            <div className="search-container">
                <div className="search-header">
                    <input 
                        type="text" 
                        className="search-input" 
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                    />
                    <button className="clear-btn" onClick={handleClear}>Clear</button>
                    <button className="close-btn" onClick={handleClear}>×</button>
                </div>

                <div className="search-content">
                    {content} 
                </div>
            </div>
        </div>
    );
};

export default Search;